import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import { BasicTooltip } from "../Tooltip";
import { OverflowDisplayTooltip } from "../Tooltip/OverflowDisplayTooltip";
import { cn } from "@/lib/utils";
import { ContextMenuItems } from "./types";
import { contextMenuItemVariants, contextMenuIconVariants } from "./variants";

interface ContextMenuItemProps<TData> {
  contextMenuItem: ContextMenuItems<TData>;
  data: TData;
}

export const ContextMenuItem = <TData,>({
  contextMenuItem,
  data,
}: ContextMenuItemProps<TData>) => {
  const {
    id,
    label,
    icon: Icon,
    className,
    getDisabled,
    getContent,
    tooltipContent,
    onClick,
    isDivider,
    variant,
    hidden,
  } = contextMenuItem;

  if (hidden) {
    return null;
  }

  if (isDivider) {
    return <div key={id} className="my-1 h-px bg-gray-200" />;
  }

  if (getContent) {
    return (
      <ContextMenuPrimitive.Item
        className={cn("cursor-default px-2 py-1", className)}
        key={id}
      >
        {getContent(data)}
      </ContextMenuPrimitive.Item>
    );
  }

  const isDisabled = getDisabled?.(data);

  return (
    <ContextMenuPrimitive.Item
      className={cn(
        contextMenuItemVariants({ variant: variant || "primary" }),
        isDisabled && "!cursor-not-allowed opacity-50 hover:!bg-transparent",
        className,
      )}
      key={id}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!isDisabled) {
          onClick?.(data);
        }
      }}
    >
      <BasicTooltip
        shouldShow={Boolean(isDisabled && tooltipContent)}
        content={tooltipContent}
      >
        <span className="flex items-center text-sm">
          {Icon && (
            <span
              className={contextMenuIconVariants({
                variant: variant || "primary",
              })}
            >
              <Icon />
            </span>
          )}
          <OverflowDisplayTooltip className="max-w-[150px]" content={label}>
            <span>{label}</span>
          </OverflowDisplayTooltip>
        </span>
      </BasicTooltip>
    </ContextMenuPrimitive.Item>
  );
};
